/* eslint-disable max-lines */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelect, useMultipleSelection } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { getTranslation } from 'common/react/utils/translations';
import { DrawerBottom } from 'common/react/components/drawer';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param props.icon
 * @param {any[]} props.items - Items.
 * @param {any[]} props.selectedItems - Selected items.
 * @param {Function} props.getItemText - Given an item, get the text that
 * represents the item.
 * @param {Function} props.getItemLabel - Given an item, get the label for the
 * item. It can be text or some JSX.
 * @param {Function} props.getItemDisabled - Get disabled state for item if required.
 * @param {Function} props.onSelectedItemsChange - Fires when selectedItems changes
 * @returns {JSX.Element} Component.
 */

const isMobile = window.matchMedia(
    `(max-width: ${PULSE.app.measurements.desktop}px)`
);

const getSelectedItem = (itemsList, targetItem) => {
    for (let item of itemsList) {
        if (_.isEqual(item, targetItem)) {
            return item;
        }
    }
    return null;
};

export const SelectMultipleWithLimit = ({
    label,
    icon,
    items,
    getItemText,
    getItemLabel,
    getItemDisabled = null,
    onSelectedItemsChange,
    getInitialSelectedItems,
    addOnResetCallback,
    counterThreshold,
    maximumSelections,
    maximumSelectionsLabel,
    title
}) => {
    const [clearActive, setClearActive] = useState(false);
    const [maxItemsSelected, setMaxItemsSelected] = useState(false);

    const {
        getDropdownProps,
        addSelectedItem,
        removeSelectedItem,
        selectedItems,
        reset
    } = useMultipleSelection({
        initialSelectedItems: getInitialSelectedItems(),
        onSelectedItemsChange: ({ selectedItems: newSelectedItems }) => {
            if (typeof onSelectedItemsChange === 'function') {
                onSelectedItemsChange(newSelectedItems);
            }
            setClearActive(true);

            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );
        },
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            switch (type) {
                case useMultipleSelection.stateChangeTypes
                    .FunctionAddSelectedItem:
                    return {
                        ...changes,
                        // De duplicate any selectedItems as it fires twice in some scenarios
                        selectedItems: [...new Set(changes.selectedItems)]
                    };
                default:
                    return changes;
            }
        }
    });

    const {
        isOpen,
        getToggleButtonProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        getLabelProps,
        closeMenu
    } = useSelect({
        items,
        selectedItem: null,
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            const selectedItemObj = getSelectedItem(
                selectedItems,
                changes.selectedItem
            );

            selectedItems?.length === maximumSelections
                ? setMaxItemsSelected(true)
                : setMaxItemsSelected(false);

            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick:
                    if (!changes.selectedItem) {
                        return changes;
                    }

                    if (
                        selectedItems.some((selectedItem) =>
                            _.isEqual(selectedItem, changes.selectedItem)
                        )
                    ) {
                        removeSelectedItem(selectedItemObj);
                    } else {
                        addSelectedItem(changes.selectedItem);
                    }

                    return {
                        ...changes,
                        isOpen: true
                    };
                case useSelect.stateChangeTypes.ToggleButtonBlur:
                    return {
                        ...changes,
                        isOpen: isMobile.matches
                            ? true
                            : !clearActive
                            ? false
                            : true
                    };
                default:
                    return changes;
            }
        }
    });

    // Unchecks selected options after 'Clear' button click
    const clearClickHandler = () => {
        reset();
        setClearActive(true);
        setMaxItemsSelected(false);
    };

    const selectRef = useRef();
    const optionsWrapper = useRef();
    const optionsWrapperEl = optionsWrapper.current;
    const unselectedItems = optionsWrapperEl?.querySelectorAll(
        'li:not(.select-multiple__option--selected)'
    );

    if (unselectedItems) {
        // If max options selected, disable all non-selected options
        if (selectedItems.length === maximumSelections) {
            for (const unselectedItem of unselectedItems) {
                unselectedItem?.classList.add(
                    'select-multiple__option--disabled'
                );
            }
        } else {
            for (const unselectedItem of unselectedItems) {
                unselectedItem?.classList.remove(
                    'select-multiple__option--disabled'
                );
            }
        }
    }

    /**
     * Check whether 'Clear' button has been selected or not as this determines the blur behaviour.
     */
    document.addEventListener('mousedown', (evt) => {
        const selectWrapper = document.querySelector(
            '.select__options-wrapper'
        );

        if (evt?.target?.classList.contains('select-multiple__header-clear')) {
            setClearActive(true);
            selectWrapper?.classList.add('u-hide');
        } else {
            setClearActive(false);
            selectWrapper?.classList.remove('u-hide');
        }
    });

    /**
     * Hookup reset method to the list of reset functions to execute when the
     * reset button is clicked.
     */
    useEffect(() => {
        addOnResetCallback(reset);
    }, []);

    const dropdownItems = (item, index) => {
        const itemText = getItemLabel(item);

        const isSelected = selectedItems.some((selectedItem) =>
            _.isEqual(selectedItem, item)
        );

        const isDisabled =
            typeof getItemDisabled === 'function'
                ? getItemDisabled(item)
                : false;

        const className = classNames('select-multiple__option', {
            'select-multiple__option--selected': isSelected,
            'select-multiple__option--highlighted': highlightedIndex === index,
            'select-multiple__option--disabled': isDisabled
        });

        const svgIcon = isSelected ? (
            <SvgIcon
                className="select-multiple__checkmark"
                icon="checkmark-square"
            />
        ) : (
            <SvgIcon
                className="select-multiple__checkmark-empty"
                icon="checkmark-square-empty"
            />
        );

        return (
            <li
                key={`${itemText}${index}`}
                {...getItemProps({
                    item,
                    index
                })}
                className={className}
            >
                {svgIcon}
                <span className="select-multiple__option-label-wrapper">
                    {getItemLabel(item)}
                </span>
            </li>
        );
    };

    const toggleButtonProps = getToggleButtonProps(
        getDropdownProps({
            onKeyDown: (event) => {
                if (
                    maxItemsSelected &&
                    (event.nativeEvent.keyCode ===
                        PULSE.app.common.CONSTANTS.KEY_CODES.SPACE ||
                        event.nativeEvent.keyCode ===
                            PULSE.app.common.CONSTANTS.KEY_CODES.ENTER)
                ) {
                    event.nativeEvent.preventDownshiftDefault = true;
                }
            }
        })
    );

    const selectedItemsLabelsReducer = (acc, item) => {
        return acc ? acc + ', ' + getItemText(item) : acc + getItemText(item);
    };

    const toggleButtonLabelText = () => {
        if (counterThreshold && counterThreshold <= selectedItems?.length) {
            return (
                <p className="counter-wrapper">
                    <span className="select__current-text">{title}</span>
                    <span className="counter counter--large">
                        {selectedItems?.length}
                    </span>
                </p>
            );
        }

        if (
            selectedItems?.length > 0 &&
            selectedItems?.length < counterThreshold
        ) {
            return selectedItems?.reduce(selectedItemsLabelsReducer, '');
        }

        return null;
    };

    const toggleButtonLabel = () => {
        return selectedItems?.length === 0 ? label : null;
    };

    const toggleButtonCurrentElement = (
        <div className="select-multiple__current">
            {icon && <SvgIcon className="select-multiple__icon" icon={icon} />}
            {toggleButtonLabel()}
            {toggleButtonLabelText()}
        </div>
    );

    const onToggleButtonBlur = (evt) => {
        if (
            (evt?.nativeEvent?.relatedTarget &&
                evt?.nativeEvent?.relatedTarget?.matches(
                    '.select-multiple__header-clear'
                )) ||
            evt?.target.getAttribute('aria-activedescendant') !== ''
        ) {
            evt.nativeEvent.preventDownshiftDefault = true;
        } else {
            evt.nativeEvent.preventDownshiftDefault = false;
        }
    };

    return (
        <div className="w-100" ref={selectRef}>
            <div
                className={classNames('select-multiple', {
                    'select-multiple--open': isOpen
                })}
            >
                <div
                    className={classNames('select-multiple__display-wrapper', {
                        'select-multiple__display-wrapper--has-selected-items':
                            !!selectedItems.length
                    })}
                >
                    <button
                        type="button"
                        aria-label={`${label?.toLowerCase()}-dropdown-button`}
                        {...toggleButtonProps}
                        className="select-multiple__display"
                        onBlur={onToggleButtonBlur}
                    >
                        {toggleButtonCurrentElement}

                        {isOpen && !selectedItems.length && (
                            <SvgIcon
                                className="select-multiple__close"
                                icon="close"
                            />
                        )}

                        {!isOpen && !selectedItems.length && (
                            <SvgIcon
                                className="select-multiple__chevron"
                                icon="chevron-down"
                            />
                        )}
                    </button>
                    {!!selectedItems.length && (
                        <button
                            className="select-multiple__reset"
                            onClick={reset}
                            aria-label={`Reset ${label} dropdown button`}
                        >
                            <SvgIcon
                                className="select-multiple__reset-icon"
                                icon="close-circle-fill"
                            />
                        </button>
                    )}
                </div>
                <div
                    className={classNames('select-multiple__options-wrapper', {
                        'u-hide': isMobile.matches
                    })}
                >
                    {isOpen ? (
                        <div
                            className={classNames('select-multiple__header', {
                                'u-hide': !isOpen && !clearActive
                            })}
                        >
                            {title}
                            {selectedItems?.length > 0 ? (
                                <button
                                    type="button"
                                    className="select-multiple__header-clear button-new button-new--secondary"
                                    onClick={() => clearClickHandler()}
                                >
                                    {getTranslation('label.clear')}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <ul
                        {...getMenuProps({}, { suppressRefError: true })}
                        className={classNames('select-multiple__options-list', {
                            'u-hide': !isOpen && !clearActive
                        })}
                        ref={optionsWrapper}
                    >
                        {selectedItems.length === maximumSelections ? (
                            <p className="select-multiple__max-threshold">
                                {maximumSelectionsLabel}
                            </p>
                        ) : null}
                        {items.map(dropdownItems)}
                    </ul>
                </div>
            </div>

            {isMobile.matches ? (
                <DrawerBottom
                    isOpen={isOpen}
                    onClose={() => closeMenu()}
                    heading={label}
                >
                    <div className="select-multiple select-multiple--mobile select-multiple--open">
                        {/*
                        An invisible label for accessibility purposes.
                    */}
                        <label className="u-hide" {...getLabelProps()}>
                            {label}
                        </label>

                        {/*
                        An invisible dummy toggle button just so we're calling
                        getToggleButtonProps to ensure rc-drawer doesn't crash.
                    */}
                        <button
                            className="u-hide"
                            {...getToggleButtonProps(getDropdownProps())}
                        ></button>

                        <div className="select-multiple__options-wrapper">
                            {selectedItems.length === maximumSelections ? (
                                <p className="select-multiple__max-threshold">
                                    {maximumSelectionsLabel}
                                </p>
                            ) : null}
                            <ul
                                {...getMenuProps(
                                    {},
                                    { suppressRefError: true }
                                )}
                                className={classNames(
                                    'select-multiple__options-list',
                                    {
                                        'u-hide': !isOpen
                                    }
                                )}
                                ref={optionsWrapper}
                            >
                                {items.map((item, index) => {
                                    const itemText = getItemLabel(item);
                                    const isSelected = selectedItems.some(
                                        (selectedItem) =>
                                            _.isEqual(selectedItem, item)
                                    );
                                    const isDisabled =
                                        typeof getItemDisabled === 'function'
                                            ? getItemDisabled(item)
                                            : false;

                                    return (
                                        <li
                                            key={`${itemText}${index}`}
                                            {...getItemProps({ item, index })}
                                            className={classNames(
                                                'select-multiple__option',
                                                {
                                                    'select-multiple__option--selected':
                                                        isSelected,
                                                    'select-multiple__option--highlighted':
                                                        highlightedIndex ===
                                                        index,
                                                    'select-multiple__option--disabled':
                                                        isDisabled
                                                }
                                            )}
                                        >
                                            {isSelected ? (
                                                <SvgIcon
                                                    className="select-multiple__checkmark"
                                                    icon="checkmark-square"
                                                />
                                            ) : (
                                                <SvgIcon
                                                    className="select-multiple__checkmark-empty"
                                                    icon="checkmark-square-empty"
                                                />
                                            )}

                                            <span className="select-multiple__option-label-wrapper">
                                                {getItemLabel(item)}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                            {isOpen && selectedItems.length > 0 ? (
                                <div className="select-multiple__header">
                                    <button
                                        type="button"
                                        className="select-multiple__header-clear button-new button-new--secondary u-margin-left-auto"
                                        onClick={() => clearClickHandler()}
                                    >
                                        {getTranslation('label.clear')}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </DrawerBottom>
            ) : null}
        </div>
    );
};

SelectMultipleWithLimit.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    getInitialSelectedItems: PropTypes.func,
    onSelectedItemsChange: PropTypes.func.isRequired,
    addOnResetCallback: PropTypes.func.isRequired,
    getItemText: PropTypes.func.isRequired,
    getItemLabel: PropTypes.func.isRequired,
    getItemDisabled: PropTypes.func,
    counterThreshold: PropTypes.number,
    isOpen: PropTypes.bool,
    title: PropTypes.string,
    maximumSelections: PropTypes.number,
    maximumSelectionsLabel: PropTypes.string
};
